import { useRef } from 'react';

import { SOFTWARE } from '@jira/platform__application-editions/src/application-key.tsx';
import { JIRA_SOFTWARE } from '@jira/platform__application-editions/src/application.tsx';
import { PREMIUM_EDITION } from '@jira/platform__application-editions/src/edition.tsx';

import { isSameDay } from 'date-fns';
import { useProductEntitlementDetails } from '@atlassian/jira-tenant-context-controller/src/components/product-entitlement-details/index.tsx';
import { useMemoizedTrialDaysLeftFromTrialEndTime } from '@atlassian/upgrades-and-purchases/get-trial-days-left';
import { useIsTimestampWithinXDays } from '../../common/date-utils.tsx';
import type { GrowthUnifiedProfileTrialType } from '../../ui/common/__generated__/ManualTrialConditionQuery.graphql.ts';
import { getReminderNudgeLastSeen, getWelcomeModalLastSeen } from '../last-seen/index.tsx';
import { useHasUsedPremiumFeaturesRecently } from '../premium-feature-usage/index.tsx';
import { useJiraProductAndEdition } from '../product-and-edition/index.tsx';

const useIsEligibleForJiraTrialAssistant = (): boolean => {
	const { currentProduct, currentEdition } = useJiraProductAndEdition();
	const productEntitlementDetails = useProductEntitlementDetails();
	const currentTimestamp = useRef(Date.now());

	const trialEndTime = productEntitlementDetails?.[SOFTWARE]?.trialEndTime;
	const billingSourceSystem = productEntitlementDetails?.[SOFTWARE]?.billingSourceSystem || 'HAMS';

	return (
		currentProduct === JIRA_SOFTWARE &&
		currentEdition === PREMIUM_EDITION &&
		billingSourceSystem === 'CCP' &&
		!!trialEndTime &&
		currentTimestamp.current < trialEndTime
	);
};

export const useJiraEntitlementId = () => {
	const { currentProduct } = useJiraProductAndEdition();
	const productEntitlementDetails = useProductEntitlementDetails();

	return currentProduct === JIRA_SOFTWARE
		? productEntitlementDetails?.[SOFTWARE]?.entitlementId
		: undefined;
};

export const useIsEligibleForTrialAssistantWelcome = () => {
	const isEligibleForExperiment = useIsEligibleForJiraTrialAssistant();
	const productEntitlementDetails = useProductEntitlementDetails();
	const modalHasBeenSeen = getWelcomeModalLastSeen();

	const trialEndTime = productEntitlementDetails?.[SOFTWARE]?.trialEndTime;
	const trialDaysRemaining = useMemoizedTrialDaysLeftFromTrialEndTime(trialEndTime) ?? 0;
	const newToTrial = trialDaysRemaining > 15;

	// Trial assistant welcome modal is shown if:
	// - eligible for experiment
	// - hasn't been on trial for more than N days
	// - and modal hasn't already been shown
	return isEligibleForExperiment && newToTrial && modalHasBeenSeen === undefined;
};

export const useIsEligibleForTrialAssistantReminder = () => {
	const isEligibleForExperiment = useIsEligibleForJiraTrialAssistant();
	const modalWasLastSeen = getWelcomeModalLastSeen() ?? 0;
	const nudgeWasLastSeen = getReminderNudgeLastSeen();
	const hasUsedPremiumFeaturesRecently = useHasUsedPremiumFeaturesRecently(3);
	const modalHasBeenSeenRecently = useIsTimestampWithinXDays(modalWasLastSeen, 3);

	// Trial assistant nudge is shown if:
	// - eligible for experiment
	// - hasn't used premium features in N days
	// - welcome modal has been shown before but not within N days
	// - and nudge hasn't already been shown
	return (
		isEligibleForExperiment &&
		hasUsedPremiumFeaturesRecently === false &&
		!!modalWasLastSeen &&
		!modalHasBeenSeenRecently &&
		!nudgeWasLastSeen
	);
};

export const useIsManualTrial = (
	trialType: GrowthUnifiedProfileTrialType | undefined,
	trialEndTimeStamp: number | undefined,
): boolean => {
	const productEntitlementDetails = useProductEntitlementDetails();

	if (
		!trialType ||
		!trialEndTimeStamp ||
		productEntitlementDetails?.[SOFTWARE]?.trialEndTime === undefined
	) {
		return false;
	}

	// Only trust GUPS trialType property if the end timestamp is also roughly the same
	return (
		isSameDay(productEntitlementDetails[SOFTWARE].trialEndTime, trialEndTimeStamp) &&
		trialType === 'MANUAL_TRIAL'
	);
};
